// ============================================================
// MOONSHINE MASTERS — moonshinePortraits.jsx        THE BOARD'S FACES (portraits of the real Toonz bodies)
//
// caP'n, 2026-10-30: the contract board's bead faces become portraits of each person's REAL 3D model ("do it now for the real 3d models").
// HOW: one small hidden <Canvas> (the PORTRAIT STUDIO) is mounted only while something is waiting for a picture. It stands one body at
// a time (moonshineFolk3D's bodyOf + the Pier's byte-identical kits), lets it settle a few frames, snaps a transparent PNG, caches it,
// and moves to the next. One extra WebGL context, only while the board is open and a face is missing. The world scene's light budget is
// untouched (the studio is its own scene with its own two lights).
// CACHE: in memory for the session, and in this browser's localStorage keyed by the kit versions - a kit bump re-shoots everyone.
// FAIL-SAFE: until (or if never) a portrait lands, <Face> keeps the old painted bead. No file, no CDN, no drain.
// ============================================================
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { IEBeing, BEING_KIT_VERSION } from "./ieBeingModels";
import { TerrorSpecies, SPECIES_KIT_VERSION } from "./ieTerrorSpecies";
import { bodyOf } from "./moonshineFolk3D";

const PX = 160;            // ⟵ DIAL  snapshot size (px, square)
const FRAME_TOP = 0.62;    // ⟵ DIAL  where the shot is centred, as a share of the body's height from the feet (higher = more face)
const FRAME_FILL = 0.78;   // ⟵ DIAL  how much of the body's height fills the picture (smaller = closer in)
const YAW = 0.45;          // ⟵ DIAL  three-quarter turn (radians) so snouts and horns read
const SETTLE = 4;          // ⟵ DIAL  frames to wait before the snap

const KEY = `moonshine_portraits_${BEING_KIT_VERSION}_${SPECIES_KIT_VERSION}`;
const CACHE = (() => { try { return JSON.parse(localStorage.getItem(KEY) || "{}") || {}; } catch (e) { return {}; } })();
const queue = [];          // ids waiting for a picture
const subs = new Set();    // everyone who wants to hear when the queue / cache changes
const ping = () => subs.forEach((f) => f());
const SAVE_CAP = 1200000;  // ⟵ DIAL  never let the portraits take more than ~1.2 MB of this browser's storage (the game save lives there too)
const save = () => { try { const j = JSON.stringify(CACHE); if (j.length < SAVE_CAP) localStorage.setItem(KEY, j); } catch (e) { /* full or private mode: session cache still works */ } };
const FAILED = new Set();  // a body that cannot be drawn is never retried this session

function request(id) {
  if (!id || CACHE[id] || FAILED.has(id) || queue.includes(id) || !bodyOf(id)) return;
  queue.push(id); ping();
}

// ⭐ a person's portrait (data URL) or null while it is being shot / if it can't be
export function usePortrait(id) {
  const [, bump] = useState(0);
  useEffect(() => { const f = () => bump((n) => n + 1); subs.add(f); request(id); return () => subs.delete(f); }, [id]);
  return (id && CACHE[id]) || null;
}

// one body on the stand: measured, framed, snapped
function Sitter({ id, onDone }) {
  const inner = useRef(), frames = useRef(0), framed = useRef(false);
  const { gl, camera, scene } = useThree();
  const b = bodyOf(id);
  useLayoutEffect(() => { frames.current = 0; framed.current = false; }, [id]);
  useFrame(() => {
    const g = inner.current;
    if (!g) return;
    if (!framed.current) {
      g.updateWorldMatrix(true, true);
      const box = new THREE.Box3().setFromObject(g);
      if (box.isEmpty()) { onDone(id, null); return; }
      const h = Math.max(0.05, box.max.y - box.min.y), w = Math.max(box.max.x - box.min.x, box.max.z - box.min.z);
      const cy = box.min.y + h * FRAME_TOP, cx = (box.min.x + box.max.x) / 2, cz = (box.min.z + box.max.z) / 2;
      const span = Math.max(h * FRAME_FILL, w * 0.9), dist = (span / 2) / Math.tan((camera.fov * Math.PI) / 360) * 1.08;
      camera.position.set(cx, cy + h * 0.04, cz + dist); camera.near = dist / 20; camera.far = dist * 20;
      camera.lookAt(cx, cy, cz); camera.updateProjectionMatrix();
      framed.current = true;
    }
    if (++frames.current < SETTLE) return;
    gl.render(scene, camera);
    let url = null;
    try { url = gl.domElement.toDataURL("image/webp", 0.85); } catch (e) { url = null; }
    onDone(id, url);
  });
  if (!b) return null;
  return (
    <group rotation={[0, YAW, 0]}>
      <group ref={inner}>{b.kit === "species" ? <TerrorSpecies being={b.being} tier={b.tier} /> : <IEBeing being={b.being} tier={b.tier} />}</group>
    </group>
  );
}

// ⭐ mount once where faces are shown (the board panel). Draws nothing when nobody is waiting.
export function PortraitStudio() {
  const [, bump] = useState(0);
  useEffect(() => { const f = () => bump((n) => n + 1); subs.add(f); return () => subs.delete(f); }, []);
  const id = queue[0];
  if (!id) return null;
  const done = (who, url) => {
    if (queue[0] !== who) return;
    queue.shift();
    if (url) { CACHE[who] = url; save(); } else FAILED.add(who);
    ping();
  };
  return (
    <div aria-hidden style={{ position: "fixed", left: -9999, top: 0, width: PX, height: PX, pointerEvents: "none", opacity: 0 }}>
      <Canvas dpr={1} gl={{ antialias: true, alpha: true, preserveDrawingBuffer: true }} camera={{ fov: 30, position: [0, 1, 4] }}
        style={{ width: PX, height: PX }} onCreated={({ gl }) => gl.setClearColor(0x000000, 0)}>
        <ambientLight intensity={0.85} />
        <directionalLight position={[2, 3, 4]} intensity={1.6} />
        <Sitter key={id} id={id} onDone={done} />
      </Canvas>
    </div>
  );
}
